import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useGeolocation } from "@/hooks/useGeolocation";
import { getCurrencyForCountry } from "@/config/countryCurrency";
import { isSupportedCurrency, type SupportedCurrency } from "@/config/exchangeRates";

interface UseUserCurrencyResult {
  currency: SupportedCurrency;
  isLoading: boolean;
}

/**
 * Determina a moeda de exibição para o utilizador com a seguinte prioridade:
 * 1. `profiles.preferred_currency` (se ≠ "auto" e ≠ null).
 * 2. Perfil verificado por KYC → país do documento.
 *    - Angola (BI) → AOA.
 *    - Passaporte estrangeiro → moeda do país do passaporte.
 * 3. Fallback: geolocalização IP (Angola → AOA; resto → USD).
 */
export function useUserCurrency(): UseUserCurrencyResult {
  const { user } = useAuth();
  const geo = useGeolocation();

  const { data: profile, isLoading } = useQuery({
    queryKey: ["user-currency-profile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data } = await supabase
        .from("profiles")
        .select("preferred_currency, kyc_country_code, is_verified, id_document_type")
        .eq("id", user.id)
        .maybeSingle();
      return data;
    },
    enabled: !!user?.id,
    staleTime: 60_000,
  });

  // 1) Preferência manual
  const pref = (profile as any)?.preferred_currency as string | null | undefined;
  if (pref && pref !== "auto" && isSupportedCurrency(pref)) {
    return { currency: pref, isLoading: false };
  }

  // 2) Perfil verificado com KYC
  if (profile && (profile as any).is_verified) {
    const country = ((profile as any).kyc_country_code || "").toUpperCase();
    const docType = (profile as any).id_document_type as string | undefined;
    if (country === "AO" || docType === "id") {
      return { currency: "AOA", isLoading: false };
    }
    if (country) {
      return { currency: getCurrencyForCountry(country), isLoading: false };
    }
  }

  // 3) Geolocalização IP
  const geoCountry = (geo?.countryCode || "").toUpperCase();
  if (geoCountry === "AO") {
    return { currency: "AOA", isLoading };
  }
  if (geoCountry) {
    return { currency: getCurrencyForCountry(geoCountry), isLoading };
  }


  return { currency: "AOA", isLoading };
}

export default useUserCurrency;
